// A real league's draft page (DRAFT_MODES.md, scenarios C1, C5, C9 and C11;
// PLAN-2026-09-26-every-screen-and-crayon P5).
//
// AN OFFLINE DRAFT (`mode: 'manual'`, D7) is held in person and entered here
// afterward by the commissioner (`DraftEntry.tsx`): the order, then each pick,
// then Finish. Everybody else reads the same page as the board fills, and
// their rosters are set when the commissioner finishes.
//
// A real league drafts by itself at its time — autopick, from everybody's
// priorities, bots in the open seats — and the board is read the next
// morning (DRAFT_MODES.md: the autopick draft is not watchable, it is read).
// So this page is the lobby before, and the board after: the order, who sits
// where, the time with how long until it, and the one thing to do before it
// (set your priorities); then every pick in rounds, with the recap one tap
// away. A league that drafts in the room is drawn by the room itself
// (`DraftRoomScreen`, D10) until it is over, and read here after.
//
// Drawn through the kit (LOOKS.md Part 7).

import { useLayoutEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  draftOrderWords,
  formatInZone,
  type DraftView,
  type PlayerSummary,
  type TeamRef,
} from '@fsp/domain';
import { useLeagueBase, useLeagueDraft, useReaderZone } from '../data/hooks';
import { EmptyState, PositionPill, ProTeamCode, TeamMark } from '../kit';
import { headDrop, scrollerOf } from '../kit/TableScroll';
import {
  Button,
  Callout,
  Caption,
  Card,
  Line,
  ListRow,
  RowName,
  RowSub,
  Stack,
  Text,
  Title,
} from '../kit/ui';
import { FromLeague } from './shared/FromLeague';
import '../styles/screens/league-draft.css';
import { PlayerName, TeamName } from './shared/Names';
import { DraftOrderEntry, DraftPickEntry, DraftSaleEntry } from './DraftEntry';

export function LeagueDraftScreen() {
  return (
    <FromLeague snapshot={useLeagueDraft()}>{(view) => <LeagueDraftBody view={view} />}</FromLeague>
  );
}

/** "in 2 days", "in 5 hours", "in 12 minutes" — how long until a time. */
function until(iso: string, now: number): string {
  const ms = Date.parse(iso) - now;
  if (ms <= 0) return 'any moment now';
  const minutes = Math.round(ms / 60000);
  if (minutes < 60) return `in ${minutes} minute${minutes === 1 ? '' : 's'}`;
  const hours = Math.round(minutes / 60);
  if (hours < 48) return `in ${hours} hour${hours === 1 ? '' : 's'}`;
  const days = Math.round(hours / 24);
  return `in ${days} days`;
}

/**
 * The newer of the read and the last answer the entry had: an answer is the
 * draft as it stands, and drawing it at once keeps the next tap from being a
 * pick behind the read that follows it.
 */
function newer(read: DraftView, answer: DraftView | null): DraftView {
  if (answer === null || answer.league.id !== read.league.id) return read;
  return Date.parse(answer.updatedAt) > Date.parse(read.updatedAt) ? answer : read;
}

/** The lobby and then the board, for a league whose draft is not held in the room. */
export function LeagueDraftBody({ view: read }: { view: DraftView }) {
  const [answer, setAnswer] = useState<DraftView | null>(null);
  const view = newer(read, answer);
  const navigate = useNavigate();
  const base = useLeagueBase();
  const zone = useReaderZone();
  const seats = [...view.seats].sort((a, b) => a.slot - b.slot);
  const done = view.status === 'complete';
  const started = view.board.some((c) => c.player !== null);
  const at =
    view.scheduledAt === null
      ? null
      : zone === null
        ? null
        : formatInZone(view.scheduledAt, zone, {
            weekday: 'short',
            month: 'short',
            day: 'numeric',
            hour: 'numeric',
            minute: '2-digit',
          });

  const manual = view.mode === 'manual';
  // THE COMMISSIONER ENTERS AN OFFLINE DRAFT; everybody else watches it fill.
  const entering = manual && view.isCommissioner && !done;
  const line = done
    ? 'The draft is done. Every pick is below, round by round.'
    : manual
      ? entering
        ? view.status === 'live'
          ? 'Enter each pick in the order it was made. Nobody’s roster changes until you finish.'
          : 'Your league drafts in person. Afterward, set the order you drafted in and enter every pick here. Rosters are set when you finish.'
        : started
          ? 'The commissioner is entering the draft. Rosters are set when they finish.'
          : `Your league drafts in person${at === null ? '' : ` on ${at}`}. Afterward, the commissioner enters every pick here, and rosters are set when they finish.`
      : view.status === 'live' || started
        ? 'The draft is running now. It takes a few seconds; the board fills as it goes.'
        : view.scheduledAt === null
          ? 'No draft time is set yet. The draft runs by itself once one is.'
          : `The draft runs by itself ${at === null ? 'at its time' : `on ${at}`}, ${until(view.scheduledAt, Date.now())}: every pick from each manager’s priorities, bots in the open seats.`;

  return (
    <div className="fsp-screen">
      <div className="fsp-pinned">
        <Line spread gap={2} wrap>
          <Stack gap={0}>
            <Title>Draft</Title>
            <Text tone="secondary" size="xs">
              {view.type === 'auction'
                ? `Auction · $${String(view.auction?.rules.budget ?? 0)} a team · ${String(view.rounds)} players`
                : `${String(view.rounds)} rounds · ${draftOrderWords(view.type).inLine}`}{' '}
              · {seats.length} teams
              {manual ? ' · offline' : ''}
            </Text>
          </Stack>
          {done ? (
            <Line gap={2}>
              {/* THE MORNING AFTER (C11): the draft played back pick by pick. */}
              <Button
                variant="primary"
                size="xs"
                icon="play"
                onClick={() => navigate(`${base}/draft/replay`)}
              >
                Watch the replay
              </Button>
              <Button size="xs" onClick={() => navigate(`${base}/draft-recap`)}>
                Draft recap
              </Button>
            </Line>
          ) : (
            <Line gap={2}>
              {/* A MOCK BEFORE ANY DRAFT, of any kind (MOCK_DRAFT.md §3): an
                  offline league's managers practice for theirs too. */}
              <Button size="xs" onClick={() => navigate(`${base}/draft/mock`)}>
                Mock draft
              </Button>
              {!manual && view.myTeamId !== null ? (
                <Button variant="primary" size="xs" onClick={() => navigate(`${base}/priorities`)}>
                  Set my priorities
                </Button>
              ) : null}
            </Line>
          )}
        </Line>
      </div>
      <div
        className="fsp-regions fsp-league-draft-regions"
        data-entering={(entering && view.status === 'live') || undefined}
      >
        {entering && view.status === 'live' ? (
          <section className="fsp-region" aria-label="Enter the draft">
            <div className="fsp-league-draft-body">
              {view.type === 'auction' ? (
                <DraftSaleEntry view={view} onView={setAnswer} />
              ) : (
                <DraftPickEntry view={view} onView={setAnswer} />
              )}
            </div>
          </section>
        ) : null}
        <section className="fsp-region" aria-label="The draft">
          <div className="fsp-league-draft-body">
            <Callout tone={done ? 'good' : 'neutral'}>{line}</Callout>
            {entering && view.status !== 'live' ? (
              <DraftOrderEntry view={view} onView={setAnswer} />
            ) : done || started || (entering && view.status === 'live') ? (
              <Board view={view} />
            ) : (
              <Order view={view} />
            )}
          </div>
        </section>
      </div>
    </div>
  );
}

function Order({ view }: { view: DraftView }) {
  const seats = [...view.seats].sort((a, b) => a.slot - b.slot);
  if (seats.length === 0) return <EmptyState>Nobody has a seat yet.</EmptyState>;
  return (
    <Stack gap={2}>
      <Caption>The order, round one</Caption>
      <Card>
        {seats.map((s) => (
          <ListRow
            key={s.team.id}
            leadSize="lg"
            selected={s.team.id === view.myTeamId}
            lead={
              <span className="fsp-league-draft-slot">
                <Text weight="bold" numeric>
                  {s.slot}
                </Text>
                <TeamMark team={s.team} size={22} />
              </span>
            }
          >
            <RowName>
              <TeamName team={s.team} />
            </RowName>
            <RowSub>{s.ai ? `${s.ai.name} · a bot drafts this seat` : s.team.ownerName}</RowSub>
          </ListRow>
        ))}
      </Card>
      <Text tone="secondary" size="xs" block>
        {view.type === 'auction'
          ? 'Teams put players up in this order, round and round.'
          : draftOrderWords(view.type).sentence}
      </Text>
    </Stack>
  );
}

function Board({ view }: { view: DraftView }) {
  return (
    <Stack gap={2}>
      <Caption>The board</Caption>
      <DraftBoardGrid
        seats={view.seats}
        rounds={view.rounds}
        auction={view.type === 'auction'}
        myTeamId={view.myTeamId}
        squares={view.board}
        current={view.board.find((c) => c.isCurrent)?.overall ?? null}
      />
    </Stack>
  );
}

/** One square of a board, as the draft page and the replay both draw it. */
export interface BoardSquare {
  overall: number;
  round: number;
  slot: number;
  teamId: string;
  originalTeamId: string | null;
  player: PlayerSummary | null;
  price: number | null;
}

/**
 * The board: rounds down, seats across. The draft page's, and the replay's
 * (C11), which hands it the squares filled so far, rings the pick on its
 * stage (`current`) and pops in the one just filled (`fresh`).
 */
export function DraftBoardGrid({
  seats: unsorted,
  rounds: count,
  auction,
  myTeamId,
  squares,
  current,
  fresh = null,
}: {
  seats: readonly { team: TeamRef; slot: number }[];
  rounds: number;
  auction: boolean;
  myTeamId: string | null;
  squares: readonly BoardSquare[];
  current: number | null;
  fresh?: number | null;
}) {
  const seats = [...unsorted].sort((a, b) => a.slot - b.slot);
  const rounds = Array.from({ length: count }, (_, i) => i + 1);
  // A COLUMN IS THE ORDER'S SEAT: a pick a trade moved stays in the column of
  // the team whose slot it was (`DraftBoardCell.originalTeamId`), as the
  // room's own board reads it. By `teamId` the original team's square was
  // blank and one of the receiver's two that round was lost.
  const cell = (round: number, teamId: string) =>
    squares.find((c) => c.round === round && (c.originalTeamId ?? c.teamId) === teamId) ?? null;
  const abbreviationOf = (teamId: string) =>
    seats.find((s) => s.team.id === teamId)?.team.abbreviation ?? '';
  // THE TEAMS' ROW STAYS IN SIGHT while the rounds scroll under it (UX-36).
  // The board scrolls sideways, which keeps a sticky head to a box that never
  // scrolls up or down, so it is moved by hand, as a table's is
  // (`TableScroll`): its rows grew a line when the name took one of its own
  // (the walk of 2026-11-01), and the head left them.
  const boardRef = useRef<HTMLDivElement>(null);
  useLayoutEffect(() => {
    const board = boardRef.current;
    const head = board?.firstElementChild;
    if (!board || !(head instanceof HTMLElement)) return;
    const scroller = scrollerOf(board);
    if (scroller === null) return;
    let frame = 0;
    const follow = () => {
      frame = 0;
      const by = headDrop(scroller, board, board, head);
      head.style.transform = by > 0 ? `translateY(${by}px)` : '';
    };
    const onScroll = () => {
      if (!frame) frame = requestAnimationFrame(follow);
    };
    scroller.addEventListener('scroll', onScroll, { passive: true });
    follow();
    return () => {
      scroller.removeEventListener('scroll', onScroll);
      if (frame) cancelAnimationFrame(frame);
    };
  }, []);
  return (
    <div className="fsp-league-draft-board" role="table" aria-label="Draft board" ref={boardRef}>
      <div className="fsp-league-draft-row fsp-league-draft-headrow" role="row">
        <span className="fsp-league-draft-round" role="columnheader">
          {auction ? '#' : 'Rd'}
        </span>
        {seats.map((s) => (
          <span
            key={s.team.id}
            className="fsp-league-draft-head"
            role="columnheader"
            data-mine={s.team.id === myTeamId || undefined}
            title={s.team.name}
          >
            <TeamMark team={s.team} size={20} />
            {/* The letters' door is a finger's 44 by padding (the audit of
                2026-10-11: 18 px wide), and their name says the team whole. */}
            {/* Not in a clipping box: it would clip the padding's press. */}
            <TeamName team={s.team} className="fsp-abbr-link fsp-league-draft-teamlink">
              {s.team.abbreviation}
            </TeamName>
          </span>
        ))}
      </div>
      {rounds.map((round) => (
        <div key={round} className="fsp-league-draft-row" role="row">
          <span className="fsp-league-draft-round" role="rowheader">
            {round}
          </span>
          {seats.map((s) => {
            const c = cell(round, s.team.id);
            const on = c !== null && c.overall === current;
            return (
              <span
                key={s.team.id}
                className="fsp-league-draft-cell"
                role="cell"
                data-mine={s.team.id === myTeamId || undefined}
                data-current={on || undefined}
                data-fresh={(c !== null && c.overall === fresh && c.player !== null) || undefined}
                aria-current={on ? 'step' : undefined}
              >
                {c?.player ? (
                  <>
                    <PositionPill pos={c.player.positions[0] ?? '—'} size="sm" />
                    {/* A pick opens his card (B-220). */}
                    <span className="fsp-league-draft-name">
                      <PlayerName player={c.player}>{c.player.shortName}</PlayerName>
                    </span>
                    {/* Outside the name's own clip, so a long name cannot hide it (B-510). */}
                    <ProTeamCode code={c.player.proTeam} />
                    {/* Made with another team's pick: whose hands it was in. */}
                    {c.originalTeamId !== null ? (
                      <Text tone="secondary" size="xs">
                        by {abbreviationOf(c.teamId)}
                      </Text>
                    ) : null}
                    {/* What he cost, in an auction (AUCTION.md §10). */}
                    {c.price !== null ? (
                      <Text tone="secondary" size="xs" numeric>
                        ${c.price}
                      </Text>
                    ) : null}
                  </>
                ) : (
                  <Text tone="muted" size="xs">
                    {c && !auction ? `${round}.${c.slot}` : ''}
                  </Text>
                )}
              </span>
            );
          })}
        </div>
      ))}
    </div>
  );
}
